<template>
  <div :class="{ error: !!errorMessage }" class="base-radio-options">
    <label
      v-for="option in options"
      :key="option.value"
      :class="{
        disabled: disabled,
        [$attrs.class as string]: true,
      }"
      tabindex="0"
    >
      <input
        type="radio"
        v-model="model"
        :value="option.value"
        :name="name"
        :disabled="disabled"
        @change="onChange(option.value)"
      />
      <span class="radiobutton" />
      <slot :option="option">
        {{ option.name }}
      </slot>
    </label>
  </div>
  <transition name="fade">
    <slot name="errorMessage" :failed-rules="errorMessage">
      <translate
        v-if="errorMessage === 'required'"
        tag="p"
        class="input-error-msg"
      >
        Please choose an option
      </translate>
    </slot>
  </transition>
</template>

<script setup lang="ts">
/* eslint-disable @typescript-eslint/no-unsafe-assignment */

/* TODO: Add proper typing to resolve type safety warnings */

import { toTypedSchema } from '@vee-validate/zod'
import { useField } from 'vee-validate'
import { ZodSchema } from 'zod'

// Disable automatic inheritance of attributes
defineOptions({
  inheritAttrs: false,
})

export type RadioOption = {
  name: string
  value: string
}
const props = withDefaults(
  defineProps<{
    name: string
    options: RadioOption[]
    validationRules?: ZodSchema
    required?: boolean
    disabled?: boolean
  }>(),
  {
    disabled: false,
    required: false,
    validationRules: undefined,
  },
)

const name = toRef(props, 'name')

const model = defineModel<string | null>({ required: true, default: null })

const validationTypedSchema = computed(() => {
  if (props.validationRules) {
    return toTypedSchema(props.validationRules)
  }
  return undefined
})

const { errorMessage, handleChange } = useField(
  name,
  validationTypedSchema.value,
  { initialValue: '' },
)

function onChange(value) {
  model.value = value
  handleChange(value)
}
</script>

<style scoped>
label {
  display: flex;
  flex-direction: row;
  align-items: center;
  justify-content: flex-start;
  cursor: pointer;
}

label ~ label {
  margin-top: 0.5em;
}

input {
  position: absolute;
  opacity: 0;
  z-index: -1;
}

.radiobutton {
  align-items: center;
  background-color: #eee;
  border-radius: 50%;
  border: 2px solid #bbb;
  display: flex;
  flex-shrink: 0;
  height: 1.4em;
  justify-content: center;
  margin-right: 0.5em;
  position: relative;
  width: 1.4em;
}

.sml .radiobutton {
  font-size: 0.75em;
}

.radiobutton:after {
  content: '';
  position: absolute;
  display: none;
  width: 0.6em;
  height: 0.6em;
  border-radius: 50%;
  background: white;
}

input:checked ~ .radiobutton:after {
  display: block;
}

input:checked ~ .radiobutton {
  background-color: var(--colour-header-dark-blue);
  border-color: var(--colour-header-dark-blue);
}

@media (hover: hover) {
  label:hover input ~ .radiobutton {
    background-color: var(--colour-page-background);
    border: 3px solid var(--colour-header-dark-blue);
  }

  label:hover input:not([disabled]):checked ~ .radiobutton {
    background-color: var(--colour-dark-blue);
    border-color: var(--colour-dark-blue);
  }
}

/* disabled state */
label.disabled {
  color: #bbb;
  pointer-events: none;
  cursor: not-allowed;
}

input:disabled ~ .radiobutton {
  background-color: #eee;
  border-color: #ddd;
  cursor: not-allowed;
}

input:disabled ~ .radiobutton:after {
  border-color: var(--colour-table-borders);
  background: #ddd;
}
</style>
